import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDocuments } from '../../hooks';
import { Button, Card, CardHeader, CardBody, Alert } from '../../components/ui';
import { DocumentType } from '../../types';

const DOCUMENT_TYPES: { value: DocumentType; label: string; description: string }[] = [
  { value: 'certificate', label: 'Certificate', description: 'Training or course certificates' },
  { value: 'license', label: 'License', description: 'Professional licenses' },
  { value: 'id_document', label: 'ID Document', description: 'Passport, national ID, driving license' },
  { value: 'police_clearance', label: 'Police Clearance', description: 'Police clearance certificate' },
  { value: 'proof_of_address', label: 'Proof of Address', description: 'Utility bill, bank statement' },
  { value: 'reference_letter', label: 'Reference Letter', description: 'Letters from previous employers' },
  { value: 'other', label: 'Other', description: 'Any other document' },
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'];

export const UploadDocument: React.FC = () => {
  const { uploadDocument, loading, error, clearError } = useDocuments();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<DocumentType>('certificate');
  const [fileError, setFileError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFileSelect = (file: File) => {
    setFileError(null);

    if (file.size > MAX_FILE_SIZE) {
      setFileError('File size must be under 10MB');
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type) && !file.name.match(/\.(pdf|jpg|jpeg|png|gif|webp)$/i)) {
      setFileError('Allowed file types: PDF, JPG, PNG, GIF, WEBP');
      return;
    }

    setSelectedFile(file);

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result as string);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      setFileError('Please select a file');
      return;
    }

    const result = await uploadDocument(selectedFile, documentType);
    if (result.success) {
      navigate('/documents');
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div>
      <Card collapsible={false}>
        <CardHeader>
          <h1 className="text-2xl font-bold">Upload Document</h1>
        </CardHeader>
        <CardBody>
          {(error || fileError) && (
            <Alert variant="danger" onClose={() => { clearError(); setFileError(null); }} className="mb-4">
              {error || fileError}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Document Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Type
              </label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {DOCUMENT_TYPES.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setDocumentType(type.value)}
                    className={`relative p-3 rounded-lg border-2 text-left transition-all ${
                      documentType === type.value
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <p className={`text-sm font-medium ${documentType === type.value ? 'text-blue-700' : 'text-gray-700'}`}>
                      {type.label}
                    </p>
                    <p className={`text-xs mt-0.5 ${documentType === type.value ? 'text-blue-500' : 'text-gray-400'}`}>
                      {type.description}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* File Drop Zone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">File</label>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                  dragOver
                    ? 'border-blue-400 bg-blue-50'
                    : selectedFile
                    ? 'border-green-300 bg-green-50'
                    : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                }`}
              >
                {selectedFile ? (
                  <div className="space-y-2">
                    {preview ? (
                      <img src={preview} alt="Preview" className="mx-auto max-h-48 rounded-lg object-contain shadow" />
                    ) : (
                      <div className="mx-auto w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center">
                        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                    <p className="text-sm font-medium text-gray-700">{selectedFile.name}</p>
                    <p className="text-xs text-gray-400">{formatSize(selectedFile.size)}</p>
                    <button
                      type="button"
                      className="text-xs text-blue-600 hover:text-blue-700"
                      onClick={(e) => { e.stopPropagation(); setSelectedFile(null); setPreview(null); }}
                    >
                      Change file
                    </button>
                  </div>
                ) : (
                  <div>
                    <svg className="mx-auto w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="text-sm font-medium text-gray-600">
                      Drop file here or <span className="text-blue-600">browse</span>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG up to 10MB</p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                onChange={handleInputChange}
                className="hidden"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button type="submit" loading={loading} disabled={!selectedFile} fullWidth className="sm:w-auto">
                Upload Document
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/documents')}
                fullWidth
                className="sm:w-auto"
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
};
